import React, { useState } from 'react';
import { Alert, AlertDescription } from '@roger/ui-components';
import { AlertTriangle } from 'lucide-react';
import {
  Navigate,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';

import {
  GroupDetailsDrawer,
  GroupEditorDrawer,
  GroupManagerHeader,
  GroupsEmptyState,
  GroupsTable,
  NewGroupDialog,
  useGroupDetail,
  useGroups,
} from '../components/group-manager';
import { FeatureFlag, PermissionSlug } from '../constants/featureFlags';
import { useDebounce } from '../hooks/useDebounce';
import { useStore } from '../store';
import { useBreadcrumbs } from '../components/breadcrumbs/BreadcrumbContext';

const SEARCH_DEBOUNCE_MS = 200;

// The route is always registered: App.tsx renders its routes before
// ProtectedRoute loads /auth/me, so a route gated there never matches a deep
// link. ProtectedRoute holds the page until the flags are loaded, and a
// flag-off agency is sent to the landing page.
const GroupManager: React.FC = () => {
  useBreadcrumbs([{ label: 'Group Manager' }]);
  const hasPermission = useStore(state => state.hasPermission);
  const hasChatFlag = useStore(state =>
    state.hasFeatureFlag(FeatureFlag.CHAT, 'workos')
  );

  if (!hasChatFlag) {
    return <Navigate to='/' replace />;
  }

  if (!hasPermission(PermissionSlug.CHAT_EDIT_GROUPS)) {
    return (
      <div>
        <Alert className='border-red-200 bg-red-50'>
          <AlertDescription className='text-red-800 flex gap-2 items-center'>
            <AlertTriangle className='h-4 w-4 text-red-600' />
            You don&apos;t have permission to access this page. Please contact
            your administrator if you need access.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return <GroupManagerContent />;
};

// Split from the permission check so a user without the slug sends no request.
const GroupManagerContent: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedGroupId = searchParams.get('group');
  const { data: selectedGroup } = useGroupDetail(selectedGroupId);

  const location = useLocation();
  const navigate = useNavigate();
  const from = (location.state as { from?: unknown } | null)?.from;

  // Manage group records the page it came from. Closing the drawer
  // goes back there; a typed URL records nothing, so it falls back to dropping
  // the ?group= param and showing the list. `replace` drops the Group Manager
  // history entry, so browser Back from the origin page does not reopen the
  // drawer.
  const closeAndReturn = () => {
    if (typeof from === 'string') {
      navigate(from, { replace: true });
      return;
    }
    setSearchParams(params => {
      params.delete('group');
      return params;
    });
  };
  const openGroup = (groupId: string) => {
    setSearchParams({ group: groupId }, { state: location.state });
  };

  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const updateDebouncedQ = useDebounce(setDebouncedQ, SEARCH_DEBOUNCE_MS);

  const { data: groups } = useGroups(debouncedQ, showArchived);

  const handleQChange = (value: string) => {
    setQ(value);
    updateDebouncedQ(value);
  };

  const [newGroupOpen, setNewGroupOpen] = useState(false);
  const handleNewGroup = () => setNewGroupOpen(true);

  return (
    <div className='flex flex-col gap-4'>
      <GroupManagerHeader
        q={q}
        onQChange={handleQChange}
        showArchived={showArchived}
        onShowArchivedChange={setShowArchived}
        onNewGroup={handleNewGroup}
      />
      {groups &&
        (groups.length === 0 && debouncedQ === '' ? (
          <GroupsEmptyState onNewGroup={handleNewGroup} />
        ) : (
          <GroupsTable rows={groups} onRowClick={openGroup} />
        ))}
      {selectedGroupId &&
        selectedGroup &&
        (selectedGroup.editable ? (
          <GroupEditorDrawer
            groupId={selectedGroupId}
            onClose={closeAndReturn}
          />
        ) : (
          <GroupDetailsDrawer
            groupId={selectedGroupId}
            onClose={closeAndReturn}
          />
        ))}
      <NewGroupDialog
        open={newGroupOpen}
        onOpenChange={setNewGroupOpen}
        onCreated={detail => openGroup(detail.id)}
      />
    </div>
  );
};

export default GroupManager;
